/**
 * LegendBar — color scale + tag legend (PR F · §F.1.6).
 *
 * Continuous mode (Heatmap): gradient of N swatches between min/max
 * with tick labels. Discrete mode (Walker / Compliance): one labelled
 * swatch per category. Both render an `aria-label` describing the
 * scale so assistive tech announces it.
 */
import type { JSX } from 'react';

export interface DiscreteLegendItem {
  label: string;
  color: string;
}

export type LegendBarProps =
  | {
      mode: 'continuous';
      min: number;
      max: number;
      ticks: number[];
      /** Base colour for the scale; defaults to var(--brand). */
      baseColor?: string;
      ariaLabel?: string;
    }
  | {
      mode: 'discrete';
      items: DiscreteLegendItem[];
      ariaLabel?: string;
    };

export function LegendBar(props: LegendBarProps): JSX.Element {
  if (props.mode === 'continuous') {
    return <ContinuousLegend {...props} />;
  }
  return <DiscreteLegend {...props} />;
}

function ContinuousLegend({
  min,
  max,
  ticks,
  baseColor = 'var(--brand)',
  ariaLabel,
}: Extract<LegendBarProps, { mode: 'continuous' }>): JSX.Element {
  const steps = ticks.length;
  // 6 opacity stops mapped across the scale.
  const opacityFor = (i: number): number =>
    steps <= 1 ? 1 : 0.10 + (i / (steps - 1)) * 0.90;
  return (
    <div
      className="legend-bar legend-bar-continuous"
      role="img"
      aria-label={ariaLabel ?? `Scale from ${min} to ${max}`}
    >
      <div className="legend-scale">
        {ticks.map((_, i) => (
          <span
            key={i}
            className="legend-scale-swatch"
            aria-hidden="true"
            style={{
              background: `color-mix(in srgb, ${baseColor} ${(opacityFor(i) * 100).toFixed(0)}%, transparent)`,
            }}
          />
        ))}
      </div>
      <div className="legend-ticks" aria-hidden="true">
        {ticks.map((t, i) => (
          <span key={i} className="mono">
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}

function DiscreteLegend({
  items,
  ariaLabel,
}: Extract<LegendBarProps, { mode: 'discrete' }>): JSX.Element {
  return (
    <div
      className="legend-bar legend-bar-discrete"
      role="img"
      aria-label={ariaLabel ?? `${items.length} category legend`}
    >
      {items.map((item) => (
        <span key={item.label} className="legend-discrete-item">
          <span
            className="legend-discrete-swatch"
            aria-hidden="true"
            style={{ background: item.color }}
          />
          <span className="legend-discrete-label">{item.label}</span>
        </span>
      ))}
    </div>
  );
}
